import { Star } from "lucide-react";
import { useSelectInputById } from "@/features/inputs/api/selectInputById";
import { cn } from "@/lib/utils";
import { BaseEntryValue } from "./BaseEntryValue";
import type { BaseEntryValueProps } from "./BaseEntryValue";

const DEFAULT_STAR_COUNT = 5;

export interface StarEntryValueProps extends Omit<BaseEntryValueProps, "value"> {
    value: number | string | null;
    inputId: string;
}

/** Read-only star rating, drawn with the same amber stars as the star input. */
export function StarEntryValue({ value, inputId, inputLabel, size, ...props }: StarEntryValueProps) {
    const { data: input } = useSelectInputById(inputId);
    const options = Array.isArray(input?.scale_options) ? input.scale_options : [];
    const starCount = options.length > 0 ? options.length : DEFAULT_STAR_COUNT;
    const rating = value === null || value === "" ? null : Number(value);

    return (
        <BaseEntryValue inputLabel={inputLabel} size={size} {...props}>
            {rating === null || !Number.isFinite(rating) ? (
                <p className="text-slate-900 dark:text-slate-200">None</p>
            ) : (
                <div className="flex items-center gap-1">
                    <span className="sr-only">{`${rating} out of ${starCount} stars`}</span>
                    {Array.from({ length: starCount }, (_, index) => (
                        <Star
                            key={index}
                            aria-hidden
                            strokeWidth={1.25}
                            className={cn(
                                size === "sm" ? "size-5" : size === "lg" ? "size-9" : "size-7",
                                index < rating ? "fill-amber-400 text-amber-500" : "text-slate-300"
                            )}
                        />
                    ))}
                </div>
            )}
        </BaseEntryValue>
    );
}
